import { Button, Select, DatePicker, Typography, Row, Col } from 'antd';
import { CSVLink } from "react-csv";
import dayjs from "dayjs";
import TablePage from "../../pages/tablePage/TablePage";
import React, {useContext, useMemo, useState} from "react";
import { getItemName } from "../../shared/utils/basic";
import { AuthContext } from "../../shared/contexts/authContext";
import DaySelect from "../../shared/components/date/DaySelect";
import CustomAreaChart from "./chart/CustomAreaChart";
import { useItemKrTextData } from "../probability/useItemData";
import ErrorPage from "../../shared/components/ErrorPage";
import IsLoading from "../../shared/components/IsLoading";
import CountryName from "../../assets/country.json";
import {useRankingData} from "./useSatistics";

const chartList = [
    { title: '시간 대 별 구매(횟수)', type: 'hourlyPurchaseCount' },
    { title: '시간 대 별 구매(금액합계)', type: 'hourlyRevenue' },
];

const ETCStatistics = () => {
    const { currentUser } = useContext(AuthContext);
    const [begin, setBegin] = useState(dayjs().add(-1, 'day').format('YYYY-MM-DD'));
    const [end, setEnd] = useState(dayjs().format('YYYY-MM-DD'));
    const [customDate, setCustomDate] = useState(dayjs().endOf('day'));
    const [chartData, setChartData] = useState(chartList[0]);
    const [index, setIndex] = useState(0);

    const { data: krItemTextData, isLoading: isKrItemTextDataLoading, error: krItemTextDataError } = useItemKrTextData();
    const { data: productRankData, isLoading: isProductRankLoading, error: productRankError } = useRankingData('product', begin, end);
    const { data: platformRankData, isLoading: isPlatformRankLoading, error: platformRankError } = useRankingData('platform', begin, end);
    const { data: byCountryRankData, isLoading: isByCountryRankLoading, error: byCountryRankError } = useRankingData('byCountry', begin, end);

    const handleSetChartData = (idx) => {
        setChartData(chartList[idx]);
        setIndex(idx);
    };

    const productRankColumns = useMemo(() => ([
        {
            title: '아이템 ID',
            dataIndex: 'itemId',
            render: (_, { itemId, productId }) => {
                return `${getItemName(krItemTextData[currentUser.target], itemId, 8, productId)} (${itemId})`;
            }
        },
        {
            title: 'ProductID',
            dataIndex: 'productId',
        },
        {
            title: '단가',
            dataIndex: 'price',
            render: (text) => {
                return `$${text?.toFixed(2)}`;
            }
        },
        {
            title: '판매 갯수',
            dataIndex: 'sales_count',
        },
        {
            title: '판매액',
            render: ({ _, sales_count, price }) => {
                return `$${(sales_count * price)?.toFixed(2)}`;
            },
            sorter: (a, b) => {
                return (a.sales_count * a.price) - (b.sales_count * b.price);
            }
        }
    ]), [currentUser.target, krItemTextData]);
    const platformRankColumns = useMemo(() =>[
        {
            title: '플랫폼',
            dataIndex: 'market',
            render: (market) => {
                return market === '' || market === null ? '합계' : market;
            }
        },
        {
            title: '판매액',
            dataIndex: 'total_sales',
            render: (text) => {
                return `$${text?.toFixed(2)}`;
            }
        }
    ], []);
    const byCountryRankColumns = useMemo(() =>[
        {
            title: '국가',
            dataIndex: 'country',
            render: (country) => {
                return `${CountryName[country]?.name_kr || ''}(${country})`;
            }
        },
        {
            title: '판매액',
            dataIndex: 'total_price',
            render: (text) => {
                return `$${text?.toFixed(2)}`;
            },
            sorter: (a, b) => a.total_price - b.total_price
        },
        {
            title: '구매자 수',
            dataIndex: 'total_count',
            sorter: (a, b) => a.total_count - b.total_count
        },
        {
            title: '매출 비율(%)',
            dataIndex: 'sales_ratio',
            render: (sales_ratio) => {
                return `${sales_ratio?.toFixed(2)}%`;
            }
        },
        {
            title: 'ARPPU($)',
            render: (_, { total_price, total_count }) => {
                const arppu = total_count === 0 ? 0 : total_price / total_count;
                return `$${arppu.toFixed(2)}`;
            }
        }
    ], []);

    const ranks = [
        {
            title: '국가 별 판매액',
            columns: byCountryRankColumns,
            type: 'byCountry',
            localData: byCountryRankData,
        },
        {
            title: '상품',
            columns: productRankColumns,
            type: 'product',
            localData: productRankData,
        },
        {
            title: '플랫폼 별 판매액',
            columns: platformRankColumns,
            type: 'platform',
            localData: platformRankData,
        },
    ];

    const isLoading = isKrItemTextDataLoading || isProductRankLoading || isPlatformRankLoading || isByCountryRankLoading;
    const error = krItemTextDataError || productRankError || platformRankError || byCountryRankError;
    if (isLoading) return <IsLoading />;
    if (error) return <ErrorPage error={error} />;

    return (
        <div style={{ margin: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 10 }}>
                <DaySelect date={customDate} setDate={setCustomDate} />
                <Select
                    value={index}
                    style={{ width: 200 }}
                    onChange={handleSetChartData}
                >
                    {chartList.map((el, idx) => (
                        <Select.Option key={idx} value={idx}>
                            {el.title}
                        </Select.Option>
                    ))}
                </Select>
            </div>
            <CustomAreaChart title={chartData.title} aspect={5} type={chartData.type}
                             dateRange={{ begin: customDate, end: customDate }} isSum={true} />
            <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                <DatePicker
                    picker="date"
                    allowClear={false}
                    value={dayjs(begin)}
                    minDate={dayjs('2024-12')}
                    maxDate={dayjs()}
                    format='YYYY-MM-DD'
                    onChange={value => setBegin(value ? value.format('YYYY-MM-DD') : begin)}
                    style={{ width: 150 }}
                />
                <DatePicker
                    picker="date"
                    allowClear={false}
                    value={dayjs(end)}
                    minDate={dayjs('2024-12')}
                    maxDate={dayjs()}
                    format='YYYY-MM-DD'
                    onChange={value => setEnd(value ? value.format('YYYY-MM-DD') : end)}
                    style={{ width: 150 }}
                />
            </div>
            {
                ranks.map((rank, idx) => (
                    idx % 2 === 0 && (
                        <Row gutter={16} key={idx}>
                            <Col xs={24} sm={12}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography.Text type="secondary">
                                        {ranks[idx].title}
                                    </Typography.Text>
                                    <CSVLink data={ranks[idx].localData}
                                             filename={`'${ranks[idx].type}'_${dayjs().format('YYYYMMDDHHmmss')}.csv`}
                                             style={{ textDecoration: 'none' }}>
                                        <Button type="primary" size="small">
                                            Download
                                        </Button>
                                    </CSVLink>
                                </div>
                                <TablePage {...ranks[idx]} />
                            </Col>
                            {idx + 1 < ranks.length && (
                                <Col xs={24} sm={12}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography.Text type="secondary">
                                            {ranks[idx + 1].title}
                                        </Typography.Text>
                                        <CSVLink data={ranks[idx + 1].localData}
                                                 filename={`'${ranks[idx + 1].type}'_${dayjs().format('YYYYMMDDHHmmss')}.csv`}
                                                 style={{ textDecoration: 'none' }}>
                                            <Button type="primary" size="small">
                                                Download
                                            </Button>
                                        </CSVLink>
                                    </div>
                                    <TablePage {...ranks[idx + 1]} />
                                </Col>
                            )}
                        </Row>
                    )
                ))
            }
        </div>
    );
}

export default ETCStatistics;

